import React, { useState, useEffect } from "react";
import axios from "axios";
import Constant from "../Components/Utils/Constant";
import { Link } from "react-router-dom";

// This component contains the fix.
const MultiSegmentCircularProgress = ({ pending, inprogress, completed, total, size = 150 }) => {
  const center = size / 2;
  const radius = (size - 20) / 2; // Leave some margin
  const circumference = 2 * Math.PI * radius;

  // Handle case where total is 0 to avoid division by zero
  const safeTotal = total || 1;

  const pendingPercentage = (pending / safeTotal) * 100;
  const inprogressPercentage = (inprogress / safeTotal) * 100;
  const completedPercentage = (completed / safeTotal) * 100;

  const pendingStroke = (pendingPercentage / 100) * circumference;
  const inprogressStroke = (inprogressPercentage / 100) * circumference;
  const completedStroke = (completedPercentage / 100) * circumference;

  const pendingOffset = 0;
  const inprogressOffset = pendingStroke;
  const completedOffset = pendingStroke + inprogressStroke;

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="transform -rotate-90">
        {/* Background circle */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke="#e5e7eb"
          strokeWidth="8"
        />
        
        {/* Pending segment (only renders if pending > 0) */}
        {pending > 0 && (
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke="#dc2626"
            strokeWidth="8"
            strokeDasharray={`${pendingStroke} ${circumference}`}
            strokeDashoffset={-pendingOffset}
            strokeLinecap="round"
          />
        )}
        
        {/* In Progress segment (only renders if inprogress > 0) */}
        {inprogress > 0 && (
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke="#fb923c"
            strokeWidth="8"
            strokeDasharray={`${inprogressStroke} ${circumference}`}
            strokeDashoffset={-inprogressOffset}
            strokeLinecap="round"
          />
        )}
        
        {/* Completed segment (only renders if completed > 0) */}
        {completed > 0 && (
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke="#16a34a"
            strokeWidth="8"
            strokeDasharray={`${completedStroke} ${circumference}`}
            strokeDashoffset={-completedOffset}
            strokeLinecap="round"
          />
        )}
      </svg>
      
      {/* Center text */}
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
        <div className="text-2xl font-bold text-gray-800">{total}</div>
        <div className="text-xs text-gray-600">Total</div>
      </div>
    </div>
  );
};

const FeedbackReview = () => {
  // State for storing fetched data, loading status, and errors
  const [dashboardData, setDashboardData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${Constant.BASE_URL}/feedback/getdashboard`);
        setDashboardData(response.data);
      } catch (err) {
        setError("Failed to fetch feedback data.");
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []); // Empty dependency array means this effect runs once when the component mounts

  // Show loading state
  if (isLoading) {
    return (
      <div className="w-[28%] lg:w-[30%] xl:w-[28%] 2xl:w-[30%] h-[500px] lg:h-[335px] xl:h-[500px] 2xl:h-[540px] flex justify-center items-center rounded-lg border border-white border-opacity-60 bg-white bg-opacity-57 shadow-md backdrop-blur-[5.7px]">
        <p className="text-gray-600">Loading Feedback...</p>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="w-[28%] lg:w-[30%] xl:w-[28%] 2xl:w-[30%] h-[500px] lg:h-[335px] xl:h-[500px] 2xl:h-[540px] flex justify-center items-center rounded-lg border border-white border-opacity-60 bg-white bg-opacity-57 shadow-md backdrop-blur-[5.7px]">
        <p className="text-red-600">{error}</p>
      </div>
    );
  }
  
  // Data processing logic
  const summary = dashboardData?.summary;
  const totalAllFeedbacks = dashboardData?.totalAllFeedbacks ?? 0;

  const appStats = summary?.app ?? { pending: 0, inprogress: 0, completed: 0 };
  const featureStats = summary?.feature ?? { pending: 0, inprogress: 0, completed: 0 };
  const outputStats = summary?.output ?? { pending: 0, inprogress: 0, completed: 0 };
  
  const appTotal = appStats.pending + appStats.inprogress + appStats.completed;
  const featureTotal = featureStats.pending + featureStats.inprogress + featureStats.completed;
  const outputTotal = outputStats.pending + outputStats.inprogress + outputStats.completed;

  const getPercentages = (stats, total) => {
    const safeTotal = total || 1;
    return {
      pending: (stats.pending / safeTotal) * 100,
      inprogress: (stats.inprogress / safeTotal) * 100,
      completed: (stats.completed / safeTotal) * 100,
    };
  };

  const appPercentages = getPercentages(appStats, appTotal);
  const featurePercentages = getPercentages(featureStats, featureTotal);
  const outputPercentages = getPercentages(outputStats, outputTotal);
  
  const totalPending = appStats.pending + featureStats.pending + outputStats.pending;
  const totalInprogress = appStats.inprogress + featureStats.inprogress + outputStats.inprogress;
  const totalCompleted = appStats.completed + featureStats.completed + outputStats.completed;

  // Render the component with fetched data
  return (
    <div className="w-[28%] lg:w-[30%] xl:w-[28%] 2xl:w-[30%] h-[500px] lg:h-[335px] xl:h-[500px] 2xl:h-[540px] flex flex-col items-center rounded-lg border border-white border-opacity-60 bg-white bg-opacity-57 shadow-md backdrop-blur-[5.7px]">
      <p className="flex text-[14px] xl:text-[14px] p-3 font-bold 2xl:text-[23px] 2xl:p-5">
        Feedback Review
      </p>
      <hr style={{ color: "#DDD" }} className="w-full border px-5" />

      <div className="py-5 lg:py-0 lg:pt-2 xl:py-5 flex flex-col items-center">
        <MultiSegmentCircularProgress
          pending={totalPending}
          inprogress={totalInprogress}
          completed={totalCompleted}
          total={totalAllFeedbacks}
          size={150}
        />
        
        {/* Legend */}
        <div className="flex flex-wrap justify-center gap-4 mt-3 text-xs">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-red-600 rounded-full"></div>
            <span>Pending ({totalPending})</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-orange-400 rounded-full"></div>
            <span>In Progress ({totalInprogress})</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-green-600 rounded-full"></div>
            <span>Completed ({totalCompleted})</span>
          </div>
        </div>
        
        <button
          className="text-center text-[14px] lg:text-[12px] xl:text-[14px] mt-2 cursor-pointer hover:underline transition-all duration-200 hover:opacity-80"
          style={{ color: "#FF9900" }}
        >
          <Link to="/appfeedback">
            View Feedbacks
          </Link>
        </button>
      </div>

      <div className="w-full flex flex-col gap-[10px] lg:gap-[0px] xl:gap-[10px] px-4 lg:px-4 xl:px-4 2xl:px-7">
        {/* App Progress */}
        <p className="lg:text-[10px] xl:text-[15px] mb-1 font-medium">App</p>
        <div className="w-full bg-gray-200 rounded-full h-2.5 flex">
          <div className="bg-red-600 h-2.5 rounded-l-full" style={{ width: `${appPercentages.pending}%` }}></div>
          <div className="bg-orange-400 h-2.5" style={{ width: `${appPercentages.inprogress}%` }}></div>
          <div className="bg-green-600 h-2.5 rounded-r-full" style={{ width: `${appPercentages.completed}%` }}></div>
        </div>

        {/* Feature Progress */}
        <p className="lg:text-[10px] xl:text-[15px] mt-2 mb-1 font-medium">Feature</p>
        <div className="w-full bg-gray-200 rounded-full h-2.5 flex">
          <div className="bg-red-600 h-2.5 rounded-l-full" style={{ width: `${featurePercentages.pending}%` }}></div>
          <div className="bg-orange-400 h-2.5" style={{ width: `${featurePercentages.inprogress}%` }}></div>
          <div className="bg-green-600 h-2.5 rounded-r-full" style={{ width: `${featurePercentages.completed}%` }}></div>
        </div>

        {/* Output Progress */}
        <p className="lg:text-[10px] xl:text-[15px] mt-2 mb-1 font-medium">Output</p>
        <div className="w-full bg-gray-200 rounded-full h-2.5 flex">
          <div className="bg-red-600 h-2.5 rounded-l-full" style={{ width: `${outputPercentages.pending}%` }}></div>
          <div className="bg-orange-400 h-2.5" style={{ width: `${outputPercentages.inprogress}%` }}></div>
          <div className="bg-green-600 h-2.5 rounded-r-full" style={{ width: `${outputPercentages.completed}%` }}></div>
        </div>
      </div>
    </div>
  );
};

export default FeedbackReview;